import classNames from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FoodTray, FoodTraySize, SelectedFoodTrayIndex } from '../../models';
import { getFoodTypeName } from '../../redux/foodtypes/selectors';
import Button, { Theme } from '../button/Button';
import Gastronom from './Gastronom';

import './Gastronom.scss';

interface Props {
  foodTrays: FoodTray[];
  selectedFoodTray?: SelectedFoodTrayIndex;
  setSelectedFoodTray?: (selectedFoodTray: SelectedFoodTrayIndex) => void;
  isOverview?: boolean;
  onAddColumnLeft?: () => void;
  onAddColumnRight?: () => void;
  onDeleteColumn?: () => void;
  disableAddButtons?: boolean;
  disableRemoveButton?: boolean;
  isTabbable?: boolean;
  isAdmin?: boolean;
}

const GastronomColumn: FC<Props> = ({
  isOverview = false,
  foodTrays,
  selectedFoodTray,
  setSelectedFoodTray,
  onAddColumnLeft,
  onAddColumnRight,
  onDeleteColumn,
  disableAddButtons = false,
  disableRemoveButton = false,
  isTabbable = true,
  isAdmin,
}) => {
  const { t } = useTranslation();

  const samePositionAsSelectedPosition = (foodTray: FoodTray) =>
    !!selectedFoodTray &&
    selectedFoodTray.columnIndex === foodTray.columnIndex &&
    selectedFoodTray.rowIndex === foodTray.rowIndex;

  const containsSelectedFoodTray = !!foodTrays.find(samePositionAsSelectedPosition);

  const getClassName = (foodTray: FoodTray) => {
    let columnClass: string;
    switch (foodTray.size) {
      case FoodTraySize.HALF:
        columnClass = 'half';
        break;
      case FoodTraySize.ONE_THIRD:
        columnClass = 'one-third';
        break;
      case FoodTraySize.TWO_THIRDS:
        columnClass = 'two-thirds';
        break;
      default:
        columnClass = 'full';
        break;
    }
    return classNames(`${columnClass} gastronom`, {
      'selected-gastronom': samePositionAsSelectedPosition(foodTray),
    });
  };

  const handleClick = (foodTray: FoodTray, event) => {
    if (setSelectedFoodTray) {
      if (!isOverview) {
        event.preventDefault();
        event.stopPropagation();
      }
      setSelectedFoodTray(foodTray);
    }
  };

  const renderGastronom = (foodTray: FoodTray, i: number) => {
    const item = isAdmin ? foodTray?.defaultFoodType : foodTray?.foodItem?.overrideFoodType || foodTray?.defaultFoodType;
    return (
      <button
        key={`${foodTray.id} - ${i}`}
        className={getClassName(foodTray)}
        onClick={event => handleClick(foodTray, event)}
        tabIndex={isTabbable ? 0 : -1}>
        {isOverview ? (
          <span className='content'>
            <p>{getFoodTypeName(item)}</p>
          </span>
        ) : (
          <Gastronom item={foodTray} />
        )}
      </button>
    );
  };

  return (
    <div className='gastronom-column'>
      {!!containsSelectedFoodTray && !!onAddColumnLeft && (
        <Button
          theme={Theme.TRANSPARANT}
          icon='InsertLeft'
          onClickCallback={onAddColumnLeft || (() => { })}
          className='add-column-left'
          isDisabled={disableAddButtons}
          tooltip={disableAddButtons ? t('tooltipAddGastronomColumnButtonDisabled') : undefined}
        />
      )}
      {!!containsSelectedFoodTray && !!onAddColumnRight && (
        <Button
          theme={Theme.TRANSPARANT}
          icon='InsertRight'
          onClickCallback={onAddColumnRight || (() => { })}
          className='add-column-right'
          isDisabled={disableAddButtons}
          tooltip={disableAddButtons ? t('tooltipAddGastronomColumnButtonDisabled') : undefined}
        />
      )}
      {foodTrays.map(renderGastronom)}
      {!!containsSelectedFoodTray && !!onDeleteColumn && (
        <Button
          theme={Theme.TRANSPARANT}
          icon='Trashcan'
          onClickCallback={onDeleteColumn || (() => { })}
          className='delete-column'
          isDisabled={disableRemoveButton}
          tooltip={disableRemoveButton ? t('tooltipRemoveGastronomColumnButtonDisabled') : undefined}
        />
      )}
    </div>
  );
};

export default GastronomColumn;
